/**
 * src/lib/hooks/crmOfflineCreate.js — offline CREATE gates for the CRM domain.
 *
 * Extracted from useCRMData.js to keep that hook under the 500-line cap while
 * mirroring the task-domain offline-create pattern (useTaskData.handleCreateTask):
 * generate a stable client uuid, OPTIMISTICALLY insert a `_pendingSync:true` row
 * into the relevant state array, durably enqueue the create with that uuid as
 * `id` (replay-idempotent — the backend honors a client-supplied id), and return
 * `{ success:true, queued:true, record }` so the caller short-circuits the network.
 *
 * Each gate is a pure-ish factory: it takes the optimistic builder + the setter +
 * the outbox op descriptor and returns the queued result (or null when NOT offline,
 * telling the caller to proceed with the online path). Observability stays in the
 * hook (logAction/logResult) so the trace shape matches every other handler.
 */

import { getOnline } from '../offline/networkStatus';
import { maybeEnqueueOffline, newOfflineId } from '../offline/enqueueOffline';

/**
 * Run the offline create gate for one CRM entity.
 *
 * @param {object} args
 * @param {boolean} args.offlineEnabled
 * @param {string}  args.type            outbox op type: 'create_vessel' | 'create_contact' | 'create_deal' | 'create_activity'
 * @param {object}  args.data            the create payload (without id)
 * @param {(id:string)=>object} args.buildOptimistic  builds the optimistic row from the new client id
 * @param {(updater:(prev:any[])=>any[])=>void} args.setState  the React setState for the target collection
 * @returns {Promise<{ queued:true, record:object } | { queued:false }>}
 *   queued=true → the op is durably queued, caller returns the record;
 *   queued=false → online, caller proceeds with the live API call.
 */
export async function offlineCreateGate({ offlineEnabled, type, data, buildOptimistic, setState }) {
  if (!offlineEnabled || getOnline()) return { queued: false };

  const id = newOfflineId();
  const record = { ...buildOptimistic(id), id, _pendingSync: true };
  setState(prev => [record, ...prev]);

  const q = await maybeEnqueueOffline({
    offlineEnabled,
    domain: 'crm',
    type,
    entity_id: id,
    payload: { ...data, id },
  });
  // maybeEnqueueOffline re-checks offline; if it somehow declined (raced back
  // online) drop the optimistic row so we don't strand an un-synced placeholder.
  if (!q.queued) {
    setState(prev => prev.filter(r => r.id !== id));
    return { queued: false };
  }
  return { queued: true, record };
}
